import type {
  AccessUser,
  GlobalFilterValues,
  PropertyReportFiltersAttributes,
  PropertyReportSubscriptionAttributes
} from 'common'

import { coreClient } from '@/api/coreclient'

export default {
  async list(admin?: boolean) {
    const client = await coreClient()
    return client.propertyReport
      .list(admin)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as PropertyReportSubscriptionAttributes[]
      })
  },
  async search(q: string, limit: number = 8) {
    const client = await coreClient()
    return client.propertyReport
      .search(q, limit)
      .then((response) => {
        return (response.data || []) as Pick<
          PropertyReportSubscriptionAttributes,
          'id' | 'user_id' | 'name' | 'subject' | 'tags'
        >[]
      })
      .catch((e) => {
        console.error(e)
        return [] as Pick<
          PropertyReportSubscriptionAttributes,
          'id' | 'user_id' | 'name' | 'subject' | 'tags'
        >[]
      })
  },
  async searchTags(q: string, limit: number = 8) {
    const client = await coreClient()
    return client.propertyReport
      .searchTags(q, limit)
      .then((response) => {
        return (response.data || []) as { label: string; count: number }[]
      })
      .catch((e) => {
        console.error(e)
        return [] as { label: string; count: number }[]
      })
  },
  async reportsByTag(tag: string) {
    const client = await coreClient()
    return client.propertyReport
      .reportsByTag(tag)
      .then((response) => {
        return response.data || []
      })
      .catch((e) => {
        console.error(e)
        return []
      })
  },
  async getByIds(ids: number[]) {
    if (!ids || ids.length === 0) {
      return [] as PropertyReportSubscriptionAttributes[]
    }
    const client = await coreClient()
    return client.propertyReport
      .byIds(ids.join(','))
      .then((response) => {
        return response.data || []
      })
      .catch((e) => {
        console.error(e)
        return [] as PropertyReportSubscriptionAttributes[]
      })
  },
  async getReport(id?: number, publicId?: string, options?: { logVisit?: boolean }) {
    const client = await coreClient()
    return client.propertyReport.getReport(id, publicId, options?.logVisit).then((response) => {
      return response.data
    })
  },
  async getFilters(reportId: number): Promise<PropertyReportFiltersAttributes | undefined | null> {
    const client = await coreClient()
    return client.propertyReport
      .getPropertyReportFilters(reportId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async updateFilters(
    reportId: number,
    filters: GlobalFilterValues
  ): Promise<PropertyReportFiltersAttributes | null> {
    const client = await coreClient()
    return client.propertyReport
      .updatePropertyReportFilters(reportId, filters)
      .then((response) => {
        return response.data ?? null
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async deleteFilters(reportId: number): Promise<boolean> {
    const client = await coreClient()
    return client.propertyReport
      .deletePropertyReportFilters(reportId)
      .then(() => true)
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async update(body: PropertyReportSubscriptionAttributes) {
    const client = await coreClient()
    return client.propertyReport
      .update(body)
      .then((response) => {
        if (response.data) {
          return response.data
        }
        return null
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async delete(id: number) {
    const client = await coreClient()
    return client.propertyReport.delete(id).then((response) => {
      return response.data
    })
  },
  async sendEmail(id: number) {
    const client = await coreClient()
    return client.propertyReport
      .sendEmail(id)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async getAccess(reportId: number) {
    const client = await coreClient()
    return client.propertyReport
      .getAccess(reportId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as AccessUser[]
      })
  },
  async createAccess(
    reportId: number,
    notify: boolean,
    data: { email: string; user_id: string | null; read_only?: boolean }[]
  ) {
    const client = await coreClient()
    return client.propertyReport
      .createAccess(reportId, notify, data)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as AccessUser[]
      })
  },
  async updateAccess(reportId: number, userId: string, readOnly: boolean) {
    const client = await coreClient()
    return client.propertyReport
      .updateAccess(reportId, userId, { read_only: readOnly })
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async deleteAccess(reportId: number, userId: string) {
    const client = await coreClient()
    return client.propertyReport
      .deleteAccess(reportId, userId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  }
}
